"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const DocumentContext = createContext();

export const DocumentProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ======================
  // GET ALL DOCUMENTS
  // ======================
  const fetchDocuments = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/documents`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch documents");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setDocuments(data.data || []);
      } else {
        setError(data.message || "Failed to fetch documents");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching documents");
    } finally {
      setLoading(false);
    }
  };

  // ======================
  // CREATE DOCUMENT REQUEST
  // ======================
  const createDocumentRequest = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/documents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to create document request",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchDocuments();

        toast.success(data.message || "Document request created successfully");

        return {
          success: true,
          message: data.message || "Document request created successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create document request",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating document request",
      };
    } finally {
      setLoading(false);
    }
  };

  // ======================
  // UPDATE STATUS
  // ======================
  const updateDocumentStatus = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(
        `${BASE_URL}/documents/updatestatus/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update document status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchDocuments();

        toast.success(data.message || "Document status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update document status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating document status",
      };
    } finally {
      setLoading(false);
    }
  };

  // ======================
  // UPDATE DOCUMENT
  // ======================
  const updateDocument = async (id, payload) => {
  setLoading(true);

  try {
    const token = getToken();

    const formData = new FormData();

    formData.append("status", payload.status);
    formData.append("remarks", payload.remarks);
    formData.append("update_audit_id", payload.update_audit_id);

    if (payload.approved_by) {
      formData.append("approved_by", payload.approved_by);
    }

    if (payload.file) {
      formData.append("file", payload.file, payload.file.name);
    }

    // Debug
    for (const pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    const res = await fetch(`${BASE_URL}/documents/${id}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await res.json();

    if (!res.ok) {
      return {
        success: false,
        message: data.message || "Failed to update document",
      };
    }

    toast.success(data.message);

    return {
      success: true,
      data,
    };
  } catch (err) {
    console.error(err);

    return {
      success: false,
      message: "Error updating document",
    };
  } finally {
    setLoading(false);
  }
};

  // ======================
  // LOAD ON MOUNT
  // ======================
  useEffect(() => {
    const token = getToken();
    if (token) fetchDocuments();
  }, []);

  return (
    <DocumentContext.Provider
      value={{
        documents,
        loading,
        error,
        fetchDocuments,
        createDocumentRequest,
        updateDocumentStatus,
        updateDocument,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
};

export const useDocuments = () => useContext(DocumentContext);